import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaHeart, FaRegHeart, FaTrophy, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useUserAuth } from '../../context/UserAuthContext';
import api from '../../services/api';
import AccessibleLightbox from './AccessibleLightbox';
import './Photos.css';

const TopPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(20);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const { user } = useUserAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchTopPhotos();
    
    // Add blue background to the body element directly
    document.body.style.backgroundColor = 'var(--tertiary-color)';
    
    // Cleanup function to reset body color when component unmounts
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  const fetchTopPhotos = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/photos/top');
      setPhotos(response.data.photos || []);
      setLimit(response.data.limit || 20);
    } catch (err) {
      console.error('Error fetching top photos:', err);
      setError('Failed to load top photos. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Local storage helper to track likes between sessions
  const getLikedPhotos = () => {
    try {
      // Include user ID in the key to keep likes separate for each user
      const key = user ? `likedPhotos_${user.uid}` : 'likedPhotos_anonymous';
      const likesStr = localStorage.getItem(key);
      return likesStr ? JSON.parse(likesStr) : {};
    } catch (error) {
      console.error('Error getting liked photos from local storage:', error);
      return {};
    }
  };

  const setLikedPhoto = (photoId, isLiked) => {
    try {
      const likedPhotos = getLikedPhotos();
      
      if (isLiked) {
        likedPhotos[photoId] = true;
      } else {
        delete likedPhotos[photoId];
      }
      
      // Include user ID in the key to keep likes separate for each user
      const key = user ? `likedPhotos_${user.uid}` : 'likedPhotos_anonymous';
      localStorage.setItem(key, JSON.stringify(likedPhotos));
    } catch (error) {
      console.error('Error saving liked photos to local storage:', error);
    }
  };

  // Apply local likes to photos when they load
  useEffect(() => {
    if (photos.length > 0 && user) {
      const likedPhotos = getLikedPhotos();
      
      // Check if any photos need to be updated with local like state
      let needsUpdate = false;
      const updatedPhotos = photos.map(photo => {
        // If locally liked but not marked as liked in the data
        if (likedPhotos[photo.id] && !photo.isLiked) {
          needsUpdate = true;
          return { ...photo, isLiked: true };
        }
        return photo;
      });
      
      if (needsUpdate) {
        setPhotos(updatedPhotos);
      }
    }
  }, [photos, user]);

  // Function to handle redirects to login with return URL
  const redirectToLogin = () => {
    // Store the current URL to return after login
    const currentPath = window.location.pathname;
    sessionStorage.setItem('returnUrl', currentPath);
    window.location.href = '/login';
  };

  const handleLike = async (photoId, isLiked) => {
    if (!user) {
      // Redirect to login if not authenticated
      redirectToLogin();
      return;
    }
    
    // Check if the local storage state already matches what the user is trying to do
    const likedPhotos = getLikedPhotos();
    const isLikedInStorage = !!likedPhotos[photoId];
    
    // Force API call after login to ensure server state is updated correctly
    const forceApiCall = true;
    
    // If trying to like a photo that's already liked in storage, skip the API call
    // If trying to unlike a photo that's not liked in storage, skip the API call
    if (forceApiCall || (!isLiked && isLikedInStorage) || (isLiked && !isLikedInStorage)) {
      // Store the original photos state for rollback if needed
      const originalPhotos = [...photos];
  
      // Update local state optimistically first
      setPhotos(photos.map(photo => {
        if (photo.id === photoId) {
          return {
            ...photo,
            likeCount: isLiked ? Math.max((photo.likeCount || 0) - 1, 0) : (photo.likeCount || 0) + 1,
            isLiked: !isLiked
          };
        }
        return photo;
      }));
  
      // Update local storage to remember this like state
      setLikedPhoto(photoId, !isLiked);
  
      try {
        let response;
        
        if (isLiked) {
          // Unlike
          response = await api.delete(`/photos/${photoId}/like`);
        } else {
          // Like
          response = await api.post(`/photos/${photoId}/like`, {});
        }
        
        // Update the like count from the server response to ensure consistency
        if (response?.data?.likeCount !== undefined) {
          setPhotos(currentPhotos => currentPhotos.map(photo => {
            if (photo.id === photoId) {
              return {
                ...photo,
                likeCount: response.data.likeCount,
                isLiked: !isLiked
              };
            }
            return photo;
          }));
        }
        
      } catch (err) {
        if (err.response && err.response.status === 400) {
          // For 400 "already liked" errors, get the current like state from the server's response
          const serverLikeState = err.response.data.message === 'User already liked this photo';
          
          // Quietly update local storage to match server state
          setLikedPhoto(photoId, serverLikeState);
          
          // Update UI silently - just make sure it matches what the server expects
          setPhotos(currentPhotos => currentPhotos.map(photo => {
            if (photo.id === photoId) {
              return {
                ...photo,
                isLiked: serverLikeState
              };
            }
            return photo;
          }));
          
          // Refresh photo data to get correct count
          const fetchPhotoData = async () => {
            try {
              const photoResponse = await api.get(`/photos/${photoId}`);
              if (photoResponse.data && photoResponse.data.photo) {
                setPhotos(currentPhotos => currentPhotos.map(photo => {
                  if (photo.id === photoId) {
                    const isLiked = photoResponse.data.photo.isLiked || false;
                    setLikedPhoto(photoId, isLiked);
                    return {
                      ...photo,
                      likeCount: photoResponse.data.photo.likeCount || 0,
                      isLiked: isLiked
                    };
                  }
                  return photo;
                }));
              }
            } catch (fetchErr) {
              // Silent error - already handled the like state above
            }
          };
          
          // Fetch in background without blocking UI
          setTimeout(fetchPhotoData, 0);
        } else if (err.response) {
          // For other errors like 401, 403, 500, etc.
          setError(`Server error. Please try again.`);
          // Revert to original state
          setPhotos(originalPhotos);
          // Revert local storage state as well
          setLikedPhoto(photoId, isLiked);
        } else {
          // Network error or other client-side issues
          setError('Network error. Please check your connection and try again.');
          // Revert to original state
          setPhotos(originalPhotos);
          // Revert local storage state as well
          setLikedPhoto(photoId, isLiked);
        }
      }
    } else {
      // State is already what the user wants - just refresh the data to ensure counts are correct
      try {
        const photoResponse = await api.get(`/photos/${photoId}`);
        if (photoResponse.data && photoResponse.data.photo) {
          setPhotos(currentPhotos => currentPhotos.map(photo => {
            if (photo.id === photoId) {
              return {
                ...photo,
                likeCount: photoResponse.data.photo.likeCount || 0
              };
            }
            return photo;
          }));
        }
      } catch (error) {
        // Silent error - just keep current state
      }
    }
  };
  
  // Open the lightbox when a photo is clicked
  const openLightbox = (index) => {
    setSelectedPhotoIndex(index);
    setLightboxOpen(true);
    // Disable body scrolling when lightbox is open
    document.body.style.overflow = 'hidden';
  };
  
  // Close the lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    // Re-enable body scrolling
    document.body.style.overflow = '';
  };

  if (loading) {
    return (
      <div className="photo-gallery-container">
        <Container className="text-center py-5">
          <Spinner animation="border" role="status" className="text-white">
            <span className="visually-hidden">{t('loading')}</span>
          </Spinner>
        </Container>
      </div>
    );
  }

  return (
    <div className="photo-gallery-container">
      <Container className="py-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <FaTrophy className="me-2 text-warning" size={30} />
            <h2 className="mb-0">{t('photoGallery.topPhotos', { count: limit })}</h2>
          </div>
          <Link to="/photos" className="btn btn-outline-light">
            {t('photoGallery.allPhotos')} <FaArrowRight className="ms-2" />
          </Link>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {photos.length === 0 && !loading ? (
          <div className="text-center py-5">
            <p>{t('photoGallery.noTopPhotos')}</p>
            <Link to="/photos" className="btn btn-outline-light">
              {t('photoGallery.viewAllPhotos')}
            </Link>
          </div>
        ) : (
          <>
            <Row xs={1} md={2} lg={4} className="g-4">
              {photos.map((photo, index) => (
                <Col key={photo.id}>
                  <Card className="photo-card h-100">
                    <div className="position-relative">
                      <div 
                        className="photo-img-container" 
                        onClick={() => openLightbox(index)}
                      >
                        <Card.Img
                          variant="top"
                          src={photo.imageUrl}
                          alt={photo.description || t('photoGallery.photoBy', { name: photo.userName })}
                          className="photo-img"
                        />
                      </div>
                      <div className="position-absolute top-0 start-0 p-2">
                        <span className="badge bg-warning text-dark">
                          <FaTrophy className="me-1" /> #{index + 1}
                        </span>
                      </div>
                    </div>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <Card.Title className="photo-uploader">
                            {photo.userName}
                          </Card.Title>
                          <Card.Text className="photo-description">
                            {photo.description || t('photoGallery.noDescription')}
                          </Card.Text>
                        </div>
                      </div>
                    </Card.Body>
                    <Card.Footer className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <Button
                          variant="link"
                          className="like-btn p-0 me-2"
                          onClick={() => user ? handleLike(photo.id, photo.isLiked) : redirectToLogin()}
                        >
                          {photo.isLiked ? <FaHeart className="text-danger" /> : <FaRegHeart className="text-secondary" />}
                        </Button>
                        <span>{photo.likeCount || 0}</span>
                      </div>
                      <small className="text-muted">
                        {new Date(photo.uploadedAt).toLocaleDateString()}
                      </small>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}
        
        {/* Accessible Lightbox Component */}
        {lightboxOpen && photos.length > 0 && (
          <AccessibleLightbox
            photos={photos}
            startIndex={selectedPhotoIndex}
            isOpen={lightboxOpen}
            onClose={closeLightbox}
            onLike={handleLike}
            user={user}
          />
        )}
      </Container>
    </div>
  );
};

export default TopPhotos;